// Reading pace for the domain check (DOMAIN_AUTHORIZATION_SPEC.md §4.4). The
// backend answers in well under a second; showing every call at once reads as
// a blur. Lines are revealed one at a time so a person can follow each query,
// its answer, and the result. Times shown are still the real server times.

import type { LogLine } from './model.ts';

/** Pause before a line appears, by what it is. */
export const PACE_MS = {
  /** A new question to a DNS server. */
  query: 650,
  /** The server's answer to the query just shown. */
  answer: 450,
  /** A conclusion (provider found, instructions ready, attention). */
  result: 700,
  /** Extra pause when the check moves on to the next sub-step. */
  nextStep: 500,
} as const;

export function lineDelay(line: LogLine, previous: LogLine | undefined): number {
  if (!previous) return 0;
  const base =
    line.glyph === '›'
      ? PACE_MS.query
      : line.glyph === '‹' || line.glyph === '·'
        ? PACE_MS.answer
        : PACE_MS.result;
  const stepChanged = (line.group ?? 0) !== (previous.group ?? 0);
  return base + (stepChanged ? PACE_MS.nextStep : 0);
}

export interface Pacer {
  push(line: LogLine): void;
  /** Resolves once every pushed line has been revealed. */
  drained(): Promise<void>;
  /** Reveals everything still waiting, immediately (Stop shows calls that already ran). */
  flush(): void;
  /** Drops anything waiting without revealing it. */
  cancel(): void;
}

export function createPacer(reveal: (line: LogLine) => void): Pacer {
  const queue: LogLine[] = [];
  let last: LogLine | undefined;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let waiters: (() => void)[] = [];

  const settle = () => {
    if (queue.length > 0 || timer) return;
    const done = waiters;
    waiters = [];
    for (const resolve of done) resolve();
  };

  const show = (line: LogLine) => {
    last = line;
    reveal(line);
  };

  const next = () => {
    timer = null;
    const line = queue.shift();
    if (!line) return settle();
    show(line);
    schedule();
  };

  const schedule = () => {
    if (timer || queue.length === 0) return settle();
    const delay = lineDelay(queue[0] as LogLine, last);
    if (delay === 0) return next();
    timer = setTimeout(next, delay);
  };

  return {
    push(line) {
      queue.push(line);
      schedule();
    },
    drained() {
      return new Promise((resolve) => {
        waiters.push(resolve);
        settle();
      });
    },
    flush() {
      if (timer) clearTimeout(timer);
      timer = null;
      while (queue.length > 0) show(queue.shift() as LogLine);
      settle();
    },
    cancel() {
      if (timer) clearTimeout(timer);
      timer = null;
      queue.length = 0;
      settle();
    },
  };
}
